import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Phone, Mail, MapPin, Loader2, Lock, Inbox, ArrowLeft, Star, Check, Clock, ImagePlus, Upload, Pencil, X } from "lucide-react";
import { LOGO_URL } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ays_admin_token";
const GALLERY_CATEGORIES = ["Retaining Walls", "Patios & Hardscape", "Driveways & Trails"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [galleryCategory, setGalleryCategory] = useState("");
  const [galleryDetails, setGalleryDetails] = useState("");
  const [galleryFile, setGalleryFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const authHeaders = useCallback((t) => ({ Authorization: `Bearer ${t}` }), []);

  const handle401 = useCallback((e) => {
    if (e.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      return true;
    }
    return false;
  }, []);

  const fetchLeads = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/leads`, { headers: authHeaders(t) });
        setLeads(data);
      } catch (e) {
        if (!handle401(e)) toast.error("Could not load leads.");
      }
    },
    [authHeaders, handle401]
  );

  const fetchReviews = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/admin/reviews`, { headers: authHeaders(t) });
        setReviews(data);
      } catch (e) {
        if (!handle401(e)) toast.error("Could not load reviews.");
      }
    },
    [authHeaders, handle401]
  );

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gallery`);
      setPhotos(data);
    } catch {
      toast.error("Could not load gallery photos.");
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchLeads(token);
      fetchReviews(token);
      fetchPhotos();
    }
  }, [token, fetchLeads, fetchReviews, fetchPhotos]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}!`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
    setReviews(null);
    setPhotos(null);
  };

  const handleDeleteLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeaders(token) });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete lead.");
    }
  };

  const handleApproveReview = async (id) => {
    try {
      await axios.post(`${API}/reviews/${id}/approve`, {}, { headers: authHeaders(token) });
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, approved: true } : r)));
      toast.success("Review published to the website.");
    } catch {
      toast.error("Could not approve review.");
    }
  };

  const handleDeleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: authHeaders(token) });
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Could not delete review.");
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    if (!galleryFile) {
      toast.error("Choose a photo first.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", galleryFile);
      formData.append("title", galleryTitle);
      formData.append("category", galleryCategory);
      formData.append("details", galleryDetails);
      await axios.post(`${API}/gallery`, formData, { headers: authHeaders(token) });
      toast.success("Photo added to the gallery.");
      setGalleryTitle("");
      setGalleryDetails("");
      setGalleryFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchPhotos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: authHeaders(token) });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      toast.success("Photo removed from the gallery.");
    } catch {
      toast.error("Could not delete photo.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B131F] flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src={LOGO_URL}
              alt="Almaguin Yard Solutions logo"
              className="h-20 w-20 rounded-full object-cover ring-2 ring-[#F5A623]/70 bg-white mx-auto"
            />
            <h1 className="font-display font-extrabold text-2xl text-white mt-5 uppercase tracking-tight">
              Owner Login
            </h1>
            <p className="text-sm text-slate-400 mt-1">View and manage your quote requests</p>
          </div>
          <form
            onSubmit={handleLogin}
            className="bg-[#142234] border border-[#1E324A] rounded-3xl p-8 space-y-5"
          >
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full bg-[#0C1827] border border-[#1E324A] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
            />
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-[#0C1827] border border-[#1E324A] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0C1827] font-display font-bold px-6 py-3.5 rounded-full hover:bg-[#E8961F] disabled:opacity-60 transition-[background-color,opacity]"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />}
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <Link
            to="/"
            className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-[#F5A623] transition-colors"
          >
            <ArrowLeft size={15} /> Back to website
          </Link>
        </div>
      </div>
    );
  }

  const pendingReviews = (reviews || []).filter((r) => !r.approved);
  const inputClass =
    "w-full bg-[#0C1827] border border-[#1E324A] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5A623]";

  return (
    <div className="min-h-screen bg-[#0B131F]" data-testid="admin-dashboard">
      <header className="bg-[#0C1827] border-b border-[#1E324A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Almaguin Yard Solutions logo"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-[#F5A623]/70 bg-white"
            />
            <div className="leading-tight">
              <span className="font-display font-extrabold text-white text-sm uppercase block">
                Owner Dashboard
              </span>
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#F5A623]">
                Almaguin Yard Solutions
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex text-sm text-slate-400 hover:text-[#F5A623] transition-colors"
            >
              View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 border border-[#1E324A] rounded-full px-4 py-2 hover:border-[#F5A623] hover:text-[#F5A623] transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-14">
        <section data-testid="admin-gallery-section">
          <div className="flex items-center gap-3 mb-5">
            <ImagePlus size={19} className="text-[#F5A623]" />
            <h2 className="font-display font-extrabold text-xl text-white uppercase">Gallery Photos</h2>
          </div>

          <form
            onSubmit={handleUploadPhoto}
            className="bg-[#142234] border border-[#1E324A] rounded-2xl p-6 mb-6"
            data-testid="admin-gallery-upload-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                data-testid="admin-gallery-title"
                required
                value={galleryTitle}
                onChange={(e) => setGalleryTitle(e.target.value)}
                placeholder="Photo title (e.g. New retaining wall, Sundridge)"
                className={inputClass}
              />
              <input
                data-testid="admin-gallery-category"
                list="gallery-category-options"
                required
                value={galleryCategory}
                onChange={(e) => setGalleryCategory(e.target.value)}
                placeholder="Category — pick one or type a new one (e.g. Hydroseeding)"
                className={inputClass}
              />
              <datalist id="gallery-category-options">
                {[...new Set([...GALLERY_CATEGORIES, ...(photos || []).map((p) => p.category)])].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <input
                data-testid="admin-gallery-details"
                value={galleryDetails}
                onChange={(e) => setGalleryDetails(e.target.value)}
                placeholder="Details (optional) — e.g. Gravel pad, South River"
                className={`${inputClass} sm:col-span-2`}
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-4 sm:items-center">
              <input
                data-testid="admin-gallery-file"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                required
                onChange={(e) => setGalleryFile(e.target.files?.[0] || null)}
                className="text-sm text-slate-300 file:mr-4 file:rounded-full file:border-0 file:bg-[#F5A623] file:px-5 file:py-2.5 file:text-sm file:font-semibold file:text-[#0C1827] hover:file:bg-[#E8961F] file:cursor-pointer"
              />
              <button
                data-testid="admin-gallery-upload-button"
                type="submit"
                disabled={uploading}
                className="inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0C1827] font-display font-bold text-sm px-6 py-3 rounded-full hover:bg-[#E8961F] disabled:opacity-60 transition-[background-color,opacity] shrink-0"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploading ? "Uploading..." : "Add to Gallery"}
              </button>
            </div>
          </form>

          {photos === null ? (
            <div className="flex justify-center py-10 text-slate-400">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : photos.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="admin-gallery-empty">
              No uploaded photos yet — photos you add here appear in the website's Fresh from the Field carousel.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  token={token}
                  onChanged={(updated) =>
                    setPhotos((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
                  }
                  onDeleted={(id) => setPhotos((prev) => prev.filter((p) => p.id !== id))}
                />
              ))}
            </div>
          )}
        </section>

        <section data-testid="admin-reviews-section">
          <div className="flex items-center gap-3 mb-5">
            <Star size={19} className="text-[#F5A623]" />
            <h2 className="font-display font-extrabold text-xl text-white uppercase">Review Moderation</h2>
            {pendingReviews.length > 0 && (
              <span className="bg-[#F5A623] text-[#0C1827] text-xs font-bold rounded-full px-2.5 py-0.5" data-testid="admin-pending-count">
                {pendingReviews.length} pending
              </span>
            )}
          </div>

          {reviews === null ? (
            <div className="flex justify-center py-10 text-slate-400">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="admin-reviews-empty">
              No reviews submitted yet. New reviews will wait here for your approval before appearing on the site.
            </p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <article
                  key={review.id}
                  data-testid="admin-review-card"
                  className="bg-[#142234] border border-[#1E324A] rounded-2xl p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display font-bold text-white">{review.name}</h3>
                        {review.approved ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2.5 py-0.5">
                            <Check size={11} /> Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F5A623] bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-full px-2.5 py-0.5" data-testid="admin-review-pending-badge">
                            <Clock size={11} /> Pending
                          </span>
                        )}
                      </div>
                      <div className="flex gap-0.5 mt-1.5">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} size={13} className="fill-[#F5A623] text-[#F5A623]" />
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!review.approved && (
                        <button
                          data-testid="admin-review-approve"
                          onClick={() => handleApproveReview(review.id)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0C1827] bg-[#F5A623] hover:bg-[#E8961F] rounded-full px-4 py-1.5 transition-colors"
                        >
                          <Check size={13} /> Approve
                        </button>
                      )}
                      <button
                        data-testid="admin-review-delete"
                        onClick={() => handleDeleteReview(review.id)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-red-400 border border-[#1E324A] hover:border-red-400/50 rounded-full px-3.5 py-1.5 transition-colors"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">"{review.text}"</p>
                  <p className="text-xs text-slate-500 mt-2">
                    {new Date(review.created_at).toLocaleString("en-CA", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                </article>
              ))}
            </div>
          )}
        </section>

        <section data-testid="admin-leads-section">
          <div className="flex items-center gap-3 mb-5">
            <Inbox size={19} className="text-[#F5A623]" />
            <h2 className="font-display font-extrabold text-xl text-white uppercase">Quote Requests</h2>
          </div>

          {leads === null ? (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-24" data-testid="admin-leads-empty">
              <Inbox size={40} className="text-[#1E324A] mx-auto mb-4" />
              <p className="font-display font-bold text-white text-lg">No quote requests yet</p>
              <p className="text-sm text-slate-400 mt-2">
                New requests from your website form will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-sm text-slate-400" data-testid="admin-leads-count">
                {leads.length} request{leads.length === 1 ? "" : "s"} total
              </p>
              {leads.map((lead) => (
                <article
                  key={lead.id}
                  data-testid="admin-lead-card"
                  className="bg-[#142234] border border-[#1E324A] rounded-2xl p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-bold text-white text-lg">{lead.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {new Date(lead.created_at).toLocaleString("en-CA", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                    <button
                      data-testid="admin-lead-delete"
                      onClick={() => handleDeleteLead(lead.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-red-400 border border-[#1E324A] hover:border-red-400/50 rounded-full px-3.5 py-1.5 transition-colors"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-slate-300 hover:text-[#F5A623]">
                      <Phone size={14} className="text-[#F5A623]" /> {lead.phone}
                    </a>
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-slate-300 hover:text-[#F5A623]">
                      <Mail size={14} className="text-[#F5A623]" /> {lead.email}
                    </a>
                    {lead.address && (
                      <span className="flex items-center gap-2 text-slate-400">
                        <MapPin size={14} className="text-[#F5A623]" /> {lead.address}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {lead.property_type && (
                      <span className="text-xs font-semibold bg-[#F5A623]/15 text-[#F5A623] border border-[#F5A623]/30 rounded-full px-3 py-1">
                        {lead.property_type}
                      </span>
                    )}
                    {lead.services.map((s) => (
                      <span
                        key={s}
                        className="text-xs font-medium text-slate-200 bg-[#0C1827] border border-[#1E324A] rounded-full px-3 py-1"
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  {lead.message && (
                    <p className="mt-4 text-sm text-slate-300 leading-relaxed border-t border-[#1E324A] pt-4">
                      {lead.message}
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const PhotoCard = ({ photo, token, onChanged, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(photo.title);
  const [category, setCategory] = useState(photo.category);
  const [details, setDetails] = useState(photo.details || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim() || category.trim().length < 2) {
      toast.error("Title and category are required.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/gallery/${photo.id}`,
        { title: title.trim(), category: category.trim(), details: details.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onChanged({
        ...photo,
        title: title.trim(),
        category: category.trim(),
        details: details.trim(),
      });
      setEditing(false);
      toast.success("Photo updated.");
    } catch {
      toast.error("Could not update photo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/gallery/${photo.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onDeleted(photo.id);
      toast.success("Photo removed from the gallery.");
    } catch {
      toast.error("Could not delete photo.");
    }
  };

  const editInputClass =
    "w-full bg-[#0C1827] border border-[#1E324A] rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5A623]";

  return (
    <figure
      data-testid="admin-gallery-photo"
      className="bg-[#142234] border border-[#1E324A] rounded-2xl overflow-hidden"
    >
      <img
        src={`${API}/gallery/file/${photo.id}`}
        alt={photo.title}
        className="w-full h-36 object-cover"
      />
      {editing ? (
        <div className="p-3 space-y-2">
          <input
            data-testid="admin-photo-edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className={editInputClass}
          />
          <input
            data-testid="admin-photo-edit-category"
            list="gallery-category-options"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Category"
            className={editInputClass}
          />
          <input
            data-testid="admin-photo-edit-details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Details (optional)"
            className={editInputClass}
          />
          <div className="flex gap-2 pt-1">
            <button
              data-testid="admin-photo-edit-save"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0C1827] bg-[#F5A623] hover:bg-[#E8961F] disabled:opacity-60 rounded-full px-3 py-1.5 transition-colors"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Save
            </button>
            <button
              data-testid="admin-photo-edit-cancel"
              onClick={() => {
                setEditing(false);
                setTitle(photo.title);
                setCategory(photo.category);
                setDetails(photo.details || "");
              }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-white border border-[#1E324A] rounded-full px-3 py-1.5 transition-colors"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <figcaption className="p-3">
          <p className="text-xs font-semibold text-white truncate">{photo.title}</p>
          {photo.details && (
            <p className="text-[11px] text-slate-400 truncate mt-0.5">{photo.details}</p>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] font-mono-accent uppercase tracking-wider text-[#F5A623]">
              {photo.category}
            </span>
            <div className="flex items-center gap-2.5">
              <button
                data-testid="admin-photo-edit-button"
                onClick={() => setEditing(true)}
                className="text-slate-500 hover:text-[#F5A623] transition-colors"
                aria-label={`Edit ${photo.title}`}
              >
                <Pencil size={14} />
              </button>
              <button
                data-testid="admin-gallery-delete"
                onClick={handleDelete}
                className="text-slate-500 hover:text-red-400 transition-colors"
                aria-label={`Delete ${photo.title}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </figcaption>
      )}
    </figure>
  );
};
